import { useCallback, useEffect, useMemo, useState } from "react"

import { Checkbox, Dropdown, Flex, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"

import Icon from "../../../Icon"
import { serviceMap, statusTextMap } from "../../../Utils/utils"
import ButtonComponent from "../../Button"
import {
  buildAvailableStatuses,
  createEmptyDraft,
  draftFromFilters,
  ENROLLMENT_SERVICES,
  ENROLLMENT_STATUSES,
  filtersFromDraft
} from "./utils"

import "../DropdownBlock.less"
import "../DropdownDateBlock/DropdownDateBlock.less"
import "./DropdownBlockEnrollments.less"

const { Text } = Typography

export default function DropdownBlockEnrollments({
  label,
  iconType,
  id,
  dropdownFilters,
  setDropdownFilters,
  disabled,
  onOpenChange
}) {
  const { t } = useTranslation()
  const isMobile = useSelector((state) => state.nav.isMobile)
  const isDisabledTransition = useSelector((state) => state.nav.disabledTransition)

  const [visible, setVisible] = useState(false)
  const [isApply, setIsApply] = useState(false)
  const [draft, setDraft] = useState(createEmptyDraft())
  const [expandedServices, setExpandedServices] = useState([])

  const availableStatuses = useMemo(() => buildAvailableStatuses(), [])

  useEffect(() => {
    if (visible) return
    setDraft(draftFromFilters(dropdownFilters, availableStatuses))
  }, [dropdownFilters, availableStatuses, visible])

  const handleVisibleChange = (nextVisible) => {
    if (nextVisible) {
      setIsApply(false)
      setDraft(draftFromFilters(dropdownFilters, availableStatuses))
    }

    if (!nextVisible && !isApply) {
      setDraft(draftFromFilters(dropdownFilters, availableStatuses))
    }

    setVisible(nextVisible)
    onOpenChange?.(nextVisible)
  }

  const toggleExpanded = (service) => {
    setExpandedServices((prev) =>
      prev.includes(service) ? prev.filter((item) => item !== service) : [...prev, service]
    )
  }

  const updateServiceStatuses = useCallback((service, nextStatuses) => {
    setDraft((prev) => ({
      ...prev,
      [service]: nextStatuses
    }))
  }, [])

  const handleToggleService = (service, checked) => {
    setExpandedServices((prev) =>
      checked
        ? prev.includes(service)
          ? prev
          : [...prev, service]
        : prev.filter((item) => item !== service)
    )

    updateServiceStatuses(service, checked ? [...availableStatuses[service]] : [])
  }

  const handleToggleAllStatuses = (service, checked) => {
    updateServiceStatuses(service, checked ? [...availableStatuses[service]] : [])
  }

  const handleToggleStatus = (service, status, checked) => {
    setDraft((prev) => ({
      ...prev,
      [service]: checked
        ? [...prev[service], status]
        : prev[service].filter((item) => item !== status)
    }))
  }

  const handleCancel = () => {
    const emptyDraft = createEmptyDraft()
    setIsApply(true)
    setDraft(emptyDraft)
    setDropdownFilters({ [id]: filtersFromDraft(emptyDraft) })
    setVisible(false)
    onOpenChange?.(false)
  }

  const handleApply = () => {
    setIsApply(true)
    setDropdownFilters({ [id]: filtersFromDraft(draft) })
    setVisible(false)
    onOpenChange?.(false)
  }

  const getSelectValue = () => {
    const appliedDraft = draftFromFilters(dropdownFilters, availableStatuses)
    const selectedServices = ENROLLMENT_SERVICES.filter((service) => appliedDraft[service].length)

    if (!selectedServices.length) {
      return `${label} ${t("All Enrollments")}`
    }

    const fullySelectedServices = selectedServices.filter(
      (service) => appliedDraft[service].length === availableStatuses[service].length
    )

    if (fullySelectedServices.length === 1 && selectedServices.length === 1) {
      return `${label}: ${
        serviceMap[fullySelectedServices[0]] || fullySelectedServices[0].toUpperCase()
      }`
    }

    if (
      fullySelectedServices.length === ENROLLMENT_SERVICES.length &&
      selectedServices.length === ENROLLMENT_SERVICES.length
    ) {
      return `${label} ${t("All Enrollments")}`
    }

    return `${label}: ${t("Mixed Values")}`
  }

  return (
    <div
      className={cls("select-block", {
        "disabled-transition": isDisabledTransition
      })}
    >
      <Dropdown
        overlayClassName="dropdown-database-wrapper"
        destroyPopupOnHide
        getPopupContainer={() => document.getElementById("main-layout")}
        onOpenChange={handleVisibleChange}
        open={visible}
        placement={isMobile ? "bottom" : "bottomLeft"}
        trigger={["click"]}
        disabled={disabled}
        dropdownRender={() => (
          <div className="dropdown-enrollments-wrapper">
            <div className="dropdown-enrollments-list">
              {ENROLLMENT_SERVICES.map((service) => {
                const selectedStatuses = draft[service]
                const allStatuses = availableStatuses[service]
                const isExpanded = expandedServices.includes(service)
                const isChecked = selectedStatuses.length > 0
                const isAllStatusesChecked = selectedStatuses.length === allStatuses.length

                return (
                  <div key={service} className="dropdown-enrollments-section">
                    <Flex
                      align="center"
                      justify="space-between"
                      className="dropdown-enrollments-section-header"
                    >
                      <Checkbox
                        checked={isChecked && isAllStatusesChecked}
                        indeterminate={isChecked && !isAllStatusesChecked}
                        onChange={(event) => handleToggleService(service, event.target.checked)}
                      >
                        <Text>{serviceMap[service] || service.toUpperCase()}</Text>
                      </Checkbox>

                      <ButtonComponent
                        type="text"
                        className="dropdown-enrollments-expand-btn"
                        icon={
                          <Icon
                            type="chevron_down_small"
                            className={cls("dropdown-enrollments-expand-icon", {
                              expanded: isExpanded
                            })}
                          />
                        }
                        onClick={() => toggleExpanded(service)}
                      />
                    </Flex>

                    {isExpanded ? (
                      <div className="dropdown-enrollments-statuses">
                        <Checkbox
                          checked={isAllStatusesChecked}
                          onChange={(event) =>
                            handleToggleAllStatuses(service, event.target.checked)
                          }
                        >
                          <Text>{t("All")}</Text>
                        </Checkbox>

                        {ENROLLMENT_STATUSES.map(({ id: statusId, label: statusLabel }) => (
                          <Checkbox
                            key={`${service}-${statusId}`}
                            checked={selectedStatuses.includes(statusId)}
                            onChange={(event) =>
                              handleToggleStatus(service, statusId, event.target.checked)
                            }
                          >
                            <Text>{statusTextMap[statusId] || statusLabel}</Text>
                          </Checkbox>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>

            <Flex className="dropdown-enrollments-buttons">
              <ButtonComponent
                className="flex center dropdown-date-button"
                text={t("Cancel")}
                onClick={handleCancel}
              />

              <ButtonComponent
                type="primary"
                className="flex center dropdown-date-button apply"
                text={t("Apply")}
                onClick={handleApply}
              />
            </Flex>
          </div>
        )}
      >
        <div
          className={cls(
            "dropdown-database",
            {
              "ant-dropdown-open": visible,
              "ant-dropdown-active": Array.isArray(dropdownFilters) && dropdownFilters.length
            },
            id
          )}
        >
          <Icon role="icon" type={iconType} />
          <Text className="dropdown-database_value">{getSelectValue()}</Text>
          <Icon type="chevron_down_small" className="arrow-icon" />
        </div>
      </Dropdown>
    </div>
  )
}
